import React, { useState, useEffect, useCallback } from 'react';
import { UserPlus, Copy, Check, Trash2, Ban, RotateCcw, AlertTriangle, ShieldCheck, Sparkles, CalendarPlus, Mail, RefreshCw } from 'lucide-react';
import supabase from '../lib/supabaseClient';

// Global admin only. Review sandbox accounts: outside evaluators get a real login on their own locked-down
// tenant. All logic lives in src/lib/reviewerTenant.js; this page is the control panel for it.

const STATUS_STYLE = {
  active: 'bg-green-50 text-green-700 border-green-200',
  disabled: 'bg-gray-100 text-gray-600 border-gray-200',
  expired: 'bg-amber-50 text-amber-700 border-amber-200',
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—');
const fmtShort = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '—');
// One shared column template for the accounts header and every row, so values line up.
const ACCOUNT_GRID = 'grid grid-cols-[minmax(200px,1fr)_76px_58px_58px_88px_76px_150px_84px_108px] gap-x-3 px-4 items-center';
const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'Never');

function generatePassword() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789';
  const bytes = new Uint32Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => chars[b % chars.length]).join('');
}

async function api(path, options = {}) {
  const { data: { session } } = await supabase.auth.getSession();
  const resp = await fetch(`/api/admin/reviewers${path}`, {
    ...options,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}`, ...(options.headers || {}) },
  });
  const body = await resp.json().catch(() => ({}));
  if (!resp.ok) throw new Error(body.error || `Request failed (${resp.status})`);
  return body;
}

function CopyButton({ text, label }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={async () => {
        try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch (e) { /* clipboard blocked */ }
      }}
      className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800"
    >
      {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
      {copied ? 'Copied' : label || 'Copy'}
    </button>
  );
}

// What prospects actually did inside their demos (src/lib/demoActivity.js). Read from a table that is NOT deleted with the demo, so
// visitors from demos that have already ended still show up here.
const PAGE_NAMES = { '/campaign-management': 'Campaigns', '/control-room': 'Pulse', '/dashboard': 'Leads', '/lead/:id': 'A lead', '/ai-studio': 'AI Studio', '/settings': 'Settings', '/calendar-settings': 'Availability' };
const videoText = (v) => (!v ? 'No record (still on it, or left)' : v.ended ? 'Watched all of it' : `Pressed Continue at ${v.pct == null ? '?' : v.pct}%`);
const fmtMin = (sec) => (sec >= 60 ? `${Math.round(sec / 60)} min` : `${sec || 0} sec`);

function DemoActivity() {
  const [visitors, setVisitors] = useState(null);
  const [err, setErr] = useState('');
  const [open, setOpen] = useState(null);

  const loadActivity = useCallback(async () => {
    try { setVisitors((await api('/demo-activity?days=60')).visitors || []); setErr(''); } catch (e) { setErr(e.message); }
  }, []);
  useEffect(() => { loadActivity(); }, [loadActivity]);

  const removeVisitor = async (v) => {
    if (!window.confirm(`Remove the activity record for ${v.contact_name || v.email || 'this visitor'}? This cannot be undone.`)) return;
    try { await api(`/demo-activity/${v.tenant_id}`, { method: 'DELETE' }); setOpen(null); await loadActivity(); } catch (e) { setErr(e.message); }
  };

  return (
    <div className="mt-8">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-lg font-semibold text-gray-900">Demo activity</h2>
        <button type="button" onClick={loadActivity} className="inline-flex items-center gap-1 text-xs text-gray-600 border border-gray-300 rounded-lg px-2.5 py-1 hover:bg-gray-50"><RefreshCw className="w-3 h-3" /> Refresh</button>
      </div>
      <p className="text-xs text-gray-500 mb-3">What each demo visitor did, last 60 days, including demos that have ended. Newest first.</p>
      {err && <div className="mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{err}. If this says the table does not exist, run supabase/20261004_demo_activity.sql in the SQL Editor.</div>}
      <div className="bg-white border border-gray-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
            <tr><th className="px-4 py-3">Visitor</th><th className="px-4 py-3">Last active</th><th className="px-4 py-3">Intro video</th><th className="px-4 py-3">Pages</th><th className="px-4 py-3">Test conversations</th><th className="px-4 py-3">Tour</th><th className="px-4 py-3">Questions</th></tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {visitors === null && <tr><td colSpan={7} className="px-4 py-6 text-center text-gray-500">Loading...</td></tr>}
            {visitors && visitors.length === 0 && <tr><td colSpan={7} className="px-4 py-6 text-center text-gray-500">No demo activity yet.</td></tr>}
            {(visitors || []).map((v) => (
              <React.Fragment key={v.tenant_id}>
                <tr className="hover:bg-gray-50 cursor-pointer" onClick={() => setOpen(open === v.tenant_id ? null : v.tenant_id)}>
                  <td className="px-4 py-3"><div className="font-medium text-gray-900">{v.contact_name || '(no name)'}</div><div className="text-xs text-gray-500">{v.email || ''}</div></td>
                  <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{fmtDateTime(v.last_at)}</td>
                  <td className="px-4 py-3 text-gray-700">{videoText(v.video)}</td>
                  <td className="px-4 py-3 text-gray-700">{v.pages.length} {v.pages.length === 1 ? 'page' : 'pages'}</td>
                  <td className="px-4 py-3 text-gray-700">{v.tests_opened ? `${v.tests_opened} opened, ${fmtMin(v.test_seconds)}` : 'None'}</td>
                  <td className="px-4 py-3 text-gray-700">{v.best_step ? (v.best_step > 3 ? 'Finished all 3' : `Reached stop ${v.best_step} of 3`) : '-'}</td>
                  <td className="px-4 py-3 text-gray-700">{v.questions.length || 'None'}</td>
                </tr>
                {open === v.tenant_id && (
                  <tr className="bg-gray-50">
                    <td colSpan={7} className="px-4 py-4 text-sm text-gray-700">
                      <div className="mb-3 text-right">
                        <button type="button" onClick={() => removeVisitor(v)} className="inline-flex items-center gap-1 text-xs text-red-700 border border-red-200 rounded-lg px-2.5 py-1 hover:bg-red-50"><Trash2 className="w-3 h-3" /> Remove this record</button>
                      </div>
                      <div className="grid gap-4 md:grid-cols-2">
                        <div>
                          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Pages visited</div>
                          {v.pages.length === 0 ? 'None' : v.pages.map((p) => <div key={p.path}>{PAGE_NAMES[p.path] || p.path} <span className="text-gray-400">x{p.count}</span></div>)}
                        </div>
                        <div>
                          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">What they asked Surf</div>
                          {v.questions.length === 0 ? 'Nothing' : v.questions.map((q, i) => <div key={i} className="mb-1">"{q.text}" <span className="text-gray-400">on {PAGE_NAMES[q.path] || q.path}, {fmtDateTime(q.at)}</span></div>)}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// The "how did you hear about us" answers (the stored key -> what the visitor chose), and why a public sign-up was blocked.
const HEARD_LABELS = {
  youtube: 'YouTube review or creator', google: 'Google search', ai_assistant: 'AI assistant (ChatGPT, Claude, Perplexity, etc.)',
  linkedin: 'LinkedIn', referral: 'Referred by a friend or colleague', directory: 'Directory or review site (G2, etc.)', other: 'Other',
};
const BLOCK_REASONS = {
  EMAIL_TAKEN: 'Email already has a demo or account', DOMAIN_TAKEN: 'Website already had a demo', PHONE_TAKEN: 'Phone already used for a demo',
  IP_TAKEN: 'Same connection already got a demo', PHONE_VOIP: 'Phone is VoIP', PHONE_LANDLINE: 'Phone is a landline', PHONE_NON_US: 'Phone is not a US number',
  PHONE_INVALID: 'Phone is not a working number', PHONE_NOT_MOBILE: 'Phone not confirmed as a US mobile', BOT: 'Hidden bot field filled in', BOT_CHECK: 'Failed the bot check',
  RATE_LIMITED: 'Too many tries from one connection', DAILY_DEMO_CAP: 'Daily demo limit reached', DAILY_CODE_CAP: 'Daily text-code limit reached', CODE_ATTEMPTS: 'Too many wrong codes',
  SMS_BLOCKED: 'Twilio refused to text that number', TOO_MANY_CODES: 'Too many codes sent to that number', PHONE_CHECK_UNAVAILABLE: 'Phone check was down',
};

// The fallbacks the build can use so a visitor never reaches a dead end, in the words shown on the page.
const FALLBACK_LABELS = {
  chat_read_reused: 'Used the chat read', questions_asked: 'Asked 2 questions', step_retried: 'AI step retried', second_campaign_generic: '2nd campaign generic',
  sample_script_generic: 'Sample chats generic', blocked_page_ignored: 'Block page ignored', knowledge_doc_skipped: 'No knowledge doc', generic_workspace: 'General workspace', system_down: 'System down',
  login_email_failed: 'Login email failed', resumed_after_restart: 'Resumed after restart',
};

// Public /demo sign-ups that were blocked: who tried, with what, and why. Read-only, nothing to approve.
function BlockedSignups() {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState('');
  const load = useCallback(async () => {
    try { setRows((await api('/demo-signups?days=30')).blocked || []); setErr(''); } catch (e) { setErr(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="mt-8">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-lg font-semibold text-gray-900">Blocked demo sign-ups</h2>
        <button type="button" onClick={load} className="inline-flex items-center gap-1 text-xs text-gray-600 border border-gray-300 rounded-lg px-2.5 py-1 hover:bg-gray-50"><RefreshCw className="w-3 h-3" /> Refresh</button>
      </div>
      <p className="text-xs text-gray-500 mb-3">Public /demo attempts that did not get a demo, last 30 days, newest first. Nothing here needs approving; this is so you can see who tried and why they were stopped.</p>
      {err && <div className="mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{err}. If this says the table does not exist, run supabase/20261020_demo_signups.sql in the SQL Editor.</div>}
      <div className="bg-white border border-gray-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
            <tr><th className="px-4 py-3">When</th><th className="px-4 py-3">Who</th><th className="px-4 py-3">Website</th><th className="px-4 py-3">Why blocked</th><th className="px-4 py-3">Came from</th></tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows === null && <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-500">Loading...</td></tr>}
            {rows && rows.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-500">Nobody has been blocked.</td></tr>}
            {(rows || []).map((r) => (
              <tr key={r.id}>
                <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{fmtDateTime(r.created_at)}</td>
                <td className="px-4 py-3"><div className="font-medium text-gray-900">{r.email || '(no email)'}</div><div className="text-xs text-gray-500">{r.name || ''}{r.phone_last4 ? ` · phone ending ${r.phone_last4}` : ''}</div></td>
                <td className="px-4 py-3 text-gray-700">{r.website || '-'}</td>
                <td className="px-4 py-3 text-gray-700"><div>{BLOCK_REASONS[r.reject_reason] || r.reject_reason}</div>{r.reject_detail && <div className="text-xs text-gray-500">{r.reject_detail}</div>}</td>
                <td className="px-4 py-3 text-gray-700 text-xs">{r.heard_from ? (HEARD_LABELS[r.heard_from] || r.heard_from) : '-'}{r.affiliate_src ? ` · ${r.affiliate_src}` : ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Reviewers() {
  const [reviewers, setReviewers] = useState([]);
  const [templateReady, setTemplateReady] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ email: '', password: '', note: '', expires_days: 14 });
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(null); // { email, password, expires_at } shown once
  const [busyId, setBusyId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleteResult, setDeleteResult] = useState(null);
  // Prospect demos: an account with NO template data and ONE campaign written from the website in the background.
  const [showDemo, setShowDemo] = useState(false);
  const [demoForm, setDemoForm] = useState({ email: '', password: '', contact_name: '', website: '', note: '', expires_days: 5 });
  const [createdDemo, setCreatedDemo] = useState(null); // { tenant_id, email, password, expires_at, website } shown until dismissed
  const [emailState, setEmailState] = useState(''); // '', 'sending', 'sent', or an error message
  const [alertMsg, setAlertMsg] = useState(''); // '', 'sending', or the result of the last test alert
  const sendTestVisitorEmail = async () => {
    setAlertMsg('sending');
    try {
      const r = await api('/test-visitor-email', { method: 'POST', body: JSON.stringify({}) });
      // The Reply-To shown is what RESEND RECORDED after the send (read back from its API), not just what we asked for.
      const recorded = Array.isArray(r.reply_to_recorded) ? `Reply-To recorded by Resend: ${r.reply_to_recorded.join(', ') || '(none)'}` : `Reply-To asked for: ${r.reply_to} (Resend did not return it yet, so it could not be confirmed${r.readback_error ? `: ${r.readback_error}` : ''})`;
      setAlertMsg(`Test visitor email sent to ${r.to}. From: ${r.from}. ${recorded}${r.id ? `. Message id ${r.id}` : ''}. Check that inbox now.`);
    } catch (e) { setAlertMsg(`The test visitor email FAILED: ${e.message}`); }
  };
  const sendTestAlert = async () => {
    setAlertMsg('sending');
    try {
      const r = await api('/test-alert', { method: 'POST', body: JSON.stringify({}) });
      setAlertMsg(`Test alert sent to ${r.to} from ${r.from}${r.id ? ` (message id ${r.id})` : ''}. Check that inbox now; if it is not there in a minute, look the id up in Resend.`);
    } catch (e) { setAlertMsg(`The test alert FAILED: ${e.message}`); }
  };
  const [extendTarget, setExtendTarget] = useState(null);
  const [extendDays, setExtendDays] = useState(5);

  const load = useCallback(async () => {
    try {
      const body = await api('');
      setReviewers(body.reviewers || []);
      setTemplateReady(!!body.template_ready);
      setError('');
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // While a demo campaign is being written (about a minute and a half) refresh every 5 seconds so the status updates itself.
  const anyBuilding = reviewers.some((r) => r.build && r.build.state === 'building');
  useEffect(() => {
    if (!anyBuilding) return undefined;
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [anyBuilding, load]);

  const openDemo = () => {
    setDemoForm({ email: '', password: generatePassword(), contact_name: '', website: '', note: '', expires_days: 5 });
    setError('');
    setShowDemo(true);
  };

  const submitDemo = async (e) => {
    e.preventDefault();
    setCreating(true);
    setError('');
    try {
      const result = await api('/demo', { method: 'POST', body: JSON.stringify(demoForm) });
      setCreatedDemo({ tenant_id: result.tenant_id, email: result.email, password: demoForm.password, expires_at: result.expires_at, website: demoForm.website.trim() });
      setEmailState('');
      setShowDemo(false);
      await load();
    } catch (err) {
      setError(err.message);
    }
    setCreating(false);
  };

  const emailLogin = async () => {
    if (!createdDemo) return;
    setEmailState('sending');
    try {
      await api(`/${createdDemo.tenant_id}/email-login`, { method: 'POST', body: JSON.stringify({ password: createdDemo.password }) });
      setEmailState('sent');
    } catch (err) {
      setEmailState(err.message);
    }
  };

  const retryBuild = async (r) => {
    setBusyId(r.tenant_id);
    setError('');
    try {
      await api(`/${r.tenant_id}/retry-build`, { method: 'POST' });
      await load();
    } catch (err) {
      setError(err.message);
    }
    setBusyId(null);
  };

  const doExtend = async () => {
    const r = extendTarget;
    setBusyId(r.tenant_id);
    setError('');
    try {
      await api(`/${r.tenant_id}/extend`, { method: 'POST', body: JSON.stringify({ days: Number(extendDays) }) });
      setExtendTarget(null);
      await load();
    } catch (err) {
      setError(err.message);
    }
    setBusyId(null);
  };

  const openAdd = () => {
    setForm({ email: '', password: generatePassword(), note: '', expires_days: 14 });
    setError('');
    setShowAdd(true);
  };

  const submitAdd = async (e) => {
    e.preventDefault();
    setCreating(true);
    setError('');
    try {
      const result = await api('', { method: 'POST', body: JSON.stringify(form) });
      setCreated({ email: result.email, password: form.password, expires_at: result.expires_at, seeded: result.seeded });
      setShowAdd(false);
      await load();
    } catch (err) {
      setError(err.message);
    }
    setCreating(false);
  };

  const toggle = async (r) => {
    setBusyId(r.tenant_id);
    setError('');
    try {
      await api(`/${r.tenant_id}/${r.status === 'disabled' ? 'enable' : 'disable'}`, { method: 'POST' });
      await load();
    } catch (err) {
      setError(err.message);
    }
    setBusyId(null);
  };

  const doDelete = async () => {
    const r = confirmDelete;
    setBusyId(r.tenant_id);
    setError('');
    try {
      const result = await api(`/${r.tenant_id}`, { method: 'DELETE' });
      setDeleteResult({ email: r.email, ...result });
      setConfirmDelete(null);
      await load();
    } catch (err) {
      setError(err.message);
      setConfirmDelete(null);
    }
    setBusyId(null);
  };

  return (
    <div className="max-w-[1600px] mx-auto p-6">
      <div className="flex items-start justify-between gap-4 mb-1">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-6 h-6 text-indigo-500" />
          <h1 className="text-xl font-bold text-gray-900">Review Accounts</h1>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={sendTestVisitorEmail} disabled={alertMsg === 'sending'} title="Sends the real visitor login email (marked TEST) to your own address, from sales@surfox.ai with reply-to sales@getsurfox.com" className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm font-medium rounded-lg disabled:opacity-60">
            <Mail className="w-4 h-4" /> Send test visitor email
          </button>
          <button onClick={sendTestAlert} disabled={alertMsg === 'sending'} title="Sends one real demo-fallback alert email so you can confirm it reaches your inbox" className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm font-medium rounded-lg disabled:opacity-60">
            <Mail className="w-4 h-4" /> {alertMsg === 'sending' ? 'Sending…' : 'Send test alert'}
          </button>
          <button onClick={openDemo} className="inline-flex items-center gap-2 px-4 py-2 border border-indigo-300 text-indigo-700 hover:bg-indigo-50 text-sm font-medium rounded-lg">
            <Sparkles className="w-4 h-4" /> Create demo account
          </button>
          <button onClick={openAdd} className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg">
            <UserPlus className="w-4 h-4" /> Add reviewer
          </button>
        </div>
      </div>
      <p className="text-sm text-gray-500 mb-6">
        Sandbox logins for outside reviewers. Each one gets their own locked-down account with sample data: they can click through everything,
        nothing they do can send a message, cost money or change your settings. Delete removes the login and everything it owns.
        A demo account is the same lock-down with no sample data: it holds one campaign written from the prospect's own website.
      </p>

      {!templateReady && (
        <div className="mb-4 flex gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>No <strong>SurFox Reviewer Template</strong> account exists yet, so new reviewers will start empty (no campaigns, leads or sample data).</span>
        </div>
      )}
      {error && <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}

      {created && (
        <div className="mb-4 rounded-lg border border-green-200 bg-green-50 p-4 text-sm">
          <div className="font-semibold text-green-800 mb-2">Reviewer created{created.seeded ? '' : ' (empty: no template yet)'}. Copy these now, the password is not shown again.</div>
          <div className="grid gap-1 text-gray-800">
            <div className="flex items-center gap-3"><span className="w-20 text-gray-500">Email</span><code>{created.email}</code><CopyButton text={created.email} /></div>
            <div className="flex items-center gap-3"><span className="w-20 text-gray-500">Password</span><code>{created.password}</code><CopyButton text={created.password} /></div>
            <div className="flex items-center gap-3"><span className="w-20 text-gray-500">Expires</span><span>{fmtDate(created.expires_at)}</span></div>
          </div>
          <button onClick={() => setCreated(null)} className="mt-3 text-xs text-gray-500 underline">Dismiss</button>
        </div>
      )}

      {alertMsg && alertMsg !== 'sending' && (
        <div className={`mb-4 text-sm rounded-lg px-3 py-2 border ${/FAILED/.test(alertMsg) ? 'text-red-700 bg-red-50 border-red-200' : 'text-green-800 bg-green-50 border-green-200'}`}>{alertMsg}</div>
      )}
      {createdDemo && (
        <div className="mb-4 rounded-lg border border-green-200 bg-green-50 p-4 text-sm">
          <div className="font-semibold text-green-800 mb-2">Demo account created. Copy these now, the password is not shown again.</div>
          <div className="grid gap-1 text-gray-800">
            <div className="flex items-center gap-3"><span className="w-20 text-gray-500">Email</span><code>{createdDemo.email}</code><CopyButton text={createdDemo.email} /></div>
            <div className="flex items-center gap-3"><span className="w-20 text-gray-500">Password</span><code>{createdDemo.password}</code><CopyButton text={createdDemo.password} /></div>
            <div className="flex items-center gap-3"><span className="w-20 text-gray-500">Sign in</span><code>surfox.ai/login</code><CopyButton text="https://surfox.ai/login" /></div>
            <div className="flex items-center gap-3"><span className="w-20 text-gray-500">Expires</span><span>{fmtDate(createdDemo.expires_at)}</span></div>
            {createdDemo.website && <div className="text-xs text-gray-600 mt-1">Writing the campaign from {createdDemo.website} now. The table below shows when it is ready.</div>}
          </div>
          <div className="mt-3 flex items-center gap-4">
            <button onClick={emailLogin} disabled={emailState === 'sending' || emailState === 'sent'} className="inline-flex items-center gap-1 px-3 py-1.5 text-xs border border-green-300 text-green-800 rounded-lg hover:bg-green-100 disabled:opacity-60">
              <Mail className="w-3.5 h-3.5" /> {emailState === 'sending' ? 'Sending…' : emailState === 'sent' ? 'Sent' : 'Email this login'}
            </button>
            {emailState && !['sending', 'sent'].includes(emailState) && <span className="text-xs text-red-700">{emailState}</span>}
            <button onClick={() => { setCreatedDemo(null); setEmailState(''); }} className="text-xs text-gray-500 underline">Dismiss</button>
          </div>
        </div>
      )}

      {deleteResult && (
        <div className={`mb-4 rounded-lg border p-4 text-sm ${deleteResult.ok ? 'border-green-200 bg-green-50 text-green-800' : 'border-red-200 bg-red-50 text-red-800'}`}>
          {deleteResult.ok
            ? <>Deleted <strong>{deleteResult.email}</strong> and verified nothing is left behind.</>
            : <>Delete of <strong>{deleteResult.email}</strong> did NOT finish. {deleteResult.error || ''} Left behind: <code>{JSON.stringify(deleteResult.leftovers)}</code></>}
          <button onClick={() => setDeleteResult(null)} className="ml-3 text-xs underline">Dismiss</button>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-xl overflow-x-auto">
        <div className="min-w-[1040px]">
          <div className={`${ACCOUNT_GRID} bg-gray-50 border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500 py-2.5 whitespace-nowrap`}>
            <div>Account</div><div>Status</div><div>Created</div><div>Expires</div><div>Last sign-in</div><div>Campaigns</div><div>Engagement</div><div>Used today</div><div className="text-right">Actions</div>
          </div>
          {loading && <div className="px-4 py-8 text-center text-sm text-gray-400">Loading…</div>}
          {!loading && reviewers.length === 0 && <div className="px-4 py-8 text-center text-sm text-gray-400">No review accounts yet.</div>}
          {reviewers.map((r) => {
            // Fixed columns shared with the header (ACCOUNT_GRID) so every value lines up. Anything noteworthy that does not fit a column (where
            // they heard of us, phone check, build progress, fallbacks) goes on one muted line under the row, and only when there is something to show.
            const isDemo = r.kind === 'prospect_demo';
            const chips = [];
            if (isDemo && r.heard_from) chips.push(<span key="heard" className="text-xs text-gray-600">Heard: {HEARD_LABELS[r.heard_from] || r.heard_from}{r.heard_other ? ` ("${r.heard_other}")` : ''}</span>);
            if (r.affiliate_src) chips.push(<span key="aff" className="inline-block px-2 py-0.5 rounded-full border text-xs bg-amber-50 text-amber-700 border-amber-200">{r.affiliate_src}</span>);
            if (isDemo && r.phone_result) chips.push(<span key="phone" className="text-xs text-gray-600">{r.phone_result}</span>);
            if (isDemo && r.rejected_before && r.rejected_before !== 'none') chips.push(<span key="rej" className="text-xs text-red-700">Blocked tries before: {r.rejected_before}</span>);
            if (r.build && r.build.state === 'building') chips.push(<span key="b1" className="text-xs text-amber-700">Writing the campaign from {r.website}…</span>);
            if (r.build && r.build.state === 'needs_input') chips.push(<span key="b2" className="text-xs text-amber-700">Waiting for the visitor's two answers (the site could not be read{r.build.detail ? `: ${r.build.detail}` : ''}). A general workspace is built if they do not answer in 5 minutes.</span>);
            if (r.build && r.build.state === 'ready') chips.push(<span key="b3" className="text-xs text-green-700">{r.build.generic ? 'General workspace ready' : r.build.site_read === true ? 'Campaign ready, built from the website' : r.build.site_read === false ? 'Campaign ready from the visitor\'s answers (the website itself was not used)' : 'Campaign ready (website use not recorded)'}{r.build.company ? `: ${r.build.company}` : ''}{r.build.login_email === 'failed' ? ' (login email FAILED)' : ''}</span>);
            if (r.build && (r.build.state === 'failed' || r.build.state === 'down')) chips.push(
              <span key="b4" className="text-xs text-red-700">
                {r.build.state === 'down' ? 'System down, nothing built' : 'Campaign NOT written'}: {r.build.error}{r.build.detail ? ` (${r.build.detail})` : ''}{' '}
                <button onClick={() => retryBuild(r)} disabled={busyId === r.tenant_id} className="inline-flex items-center gap-1 underline disabled:opacity-50"><RefreshCw className="w-3 h-3" /> Retry</button>
              </span>
            );
            if (r.build && Array.isArray(r.build.fallbacks)) r.build.fallbacks.forEach((f) => chips.push(
              <span key={`fb-${f.kind}`} title={`${f.at || ''} ${f.detail || ''}`} className="inline-block px-2 py-0.5 rounded-full border text-xs bg-amber-50 text-amber-800 border-amber-200">{FALLBACK_LABELS[f.kind] || f.kind}</span>
            ));
            const iconBtn = 'inline-flex items-center justify-center w-8 h-8 border rounded-lg disabled:opacity-50';
            const tick = (n) => (n > 0 ? 'text-green-700' : 'text-amber-600');
            return (
              <div key={r.tenant_id} className="border-b border-gray-100 last:border-b-0 hover:bg-gray-50/60">
                <div className={`${ACCOUNT_GRID} py-2.5`}>
                  <div className="min-w-0">
                    <div className="font-medium text-gray-900 truncate" title={r.email || ''}>{r.email || '(no login)'}</div>
                    <div className="flex items-center gap-2 min-w-0">
                      <span className={`flex-shrink-0 px-1.5 rounded border text-[11px] leading-4 ${isDemo ? 'bg-indigo-50 text-indigo-700 border-indigo-200' : 'bg-gray-50 text-gray-600 border-gray-200'}`}>{isDemo ? 'Demo' : 'Reviewer'}</span>
                      {r.note && <span className="text-xs text-gray-500 truncate" title={r.note}>{r.note}</span>}
                    </div>
                  </div>
                  <div><span className={`inline-block px-2 py-0.5 rounded-full border text-xs ${STATUS_STYLE[r.status]}`}>{r.status}</span></div>
                  <div className="text-xs text-gray-600 whitespace-nowrap" title={fmtDate(r.created_at)}>{fmtShort(r.created_at)}</div>
                  <div className="text-xs text-gray-600 whitespace-nowrap" title={fmtDate(r.expires_at)}>{fmtShort(r.expires_at)}</div>
                  <div className="text-gray-600 text-xs leading-snug whitespace-nowrap">{r.last_sign_in_at ? <>{fmtShort(r.last_sign_in_at)}<br />{new Date(r.last_sign_in_at).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}</> : <span className="text-gray-400">Never</span>}</div>
                  <div className="text-xs text-gray-600 whitespace-nowrap">{r.campaigns} / 5</div>
                  <div className="text-xs leading-snug whitespace-nowrap">
                    {!r.signed_in ? <span className="text-gray-400">Never signed in</span> : (
                      <>
                        <div className={tick(r.test_conversations)}>{r.test_conversations > 0 ? '✓' : '○'} Test chats ({r.test_conversations})</div>
                        <div className={tick(r.surf_conversations)}>{r.surf_conversations > 0 ? '✓' : '○'} Talked to Surf ({r.surf_conversations})</div>
                      </>
                    )}
                  </div>
                  <div className="text-xs leading-snug text-gray-600 whitespace-nowrap" title="Sandbox test turns today and Surf messages today, against their daily limits">
                    <div>{r.sandbox_turns_today}/{r.sandbox_turn_limit || 150} tests</div>
                    <div>{r.surf_messages_today}/40 Surf</div>
                  </div>
                  <div className="flex justify-end gap-1.5">
                    <button onClick={() => { setExtendDays(5); setExtendTarget(r); }} disabled={busyId === r.tenant_id} title="Extend" aria-label="Extend" className={`${iconBtn} border-gray-300 text-gray-700 hover:bg-gray-50`}><CalendarPlus className="w-4 h-4" /></button>
                    <button onClick={() => toggle(r)} disabled={busyId === r.tenant_id} title={r.status === 'disabled' ? 'Enable' : 'Disable'} aria-label={r.status === 'disabled' ? 'Enable' : 'Disable'} className={`${iconBtn} border-gray-300 text-gray-700 hover:bg-gray-50`}>
                      {r.status === 'disabled' ? <RotateCcw className="w-4 h-4" /> : <Ban className="w-4 h-4" />}
                    </button>
                    <button onClick={() => setConfirmDelete(r)} disabled={busyId === r.tenant_id} title="Delete" aria-label="Delete" className={`${iconBtn} border-red-200 text-red-600 hover:bg-red-50`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
                {chips.length > 0 && <div className="px-4 pb-2.5 -mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">{chips}</div>}
              </div>
            );
          })}
        </div>
      </div>

      <DemoActivity />
      <BlockedSignups />

      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <form onSubmit={submitAdd} className="w-full max-w-md bg-white rounded-xl shadow-xl p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Add reviewer</h2>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input type="email" required autoFocus value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full mb-4 px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="reviewer@example.com" />
            <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <div className="flex gap-2 mb-1">
              <input type="text" required minLength={10} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono" />
              <button type="button" onClick={() => setForm({ ...form, password: generatePassword() })} className="px-3 py-2 text-xs border border-gray-300 rounded-lg hover:bg-gray-50">Generate</button>
            </div>
            <div className="mb-4"><CopyButton text={form.password} label="Copy password" /></div>
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Note (only you see this)</label>
                <input type="text" maxLength={200} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="e.g. HubSpot video" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Days</label>
                <input type="number" min={1} max={90} value={form.expires_days} onChange={(e) => setForm({ ...form, expires_days: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              </div>
            </div>
            <p className="text-xs text-gray-500 mb-4">Creates a locked-down account with sample data. Messaging is permanently off and there is no billing. This takes about a minute.</p>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setShowAdd(false)} disabled={creating} className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
              <button type="submit" disabled={creating} className="px-4 py-2 text-sm bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg disabled:opacity-60">{creating ? 'Creating…' : 'Create account'}</button>
            </div>
          </form>
        </div>
      )}

      {showDemo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <form onSubmit={submitDemo} className="w-full max-w-md bg-white rounded-xl shadow-xl p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Create demo account</h2>
            <label className="block text-sm font-medium text-gray-700 mb-1">Prospect email</label>
            <input type="email" required autoFocus value={demoForm.email} onChange={(e) => setDemoForm({ ...demoForm, email: e.target.value })}
              className="w-full mb-4 px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="name@theircompany.com" />
            <label className="block text-sm font-medium text-gray-700 mb-1">Prospect name (optional)</label>
            <input type="text" maxLength={80} value={demoForm.contact_name} onChange={(e) => setDemoForm({ ...demoForm, contact_name: e.target.value })}
              className="w-full mb-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="e.g. Maria Lopez" />
            <p className="text-xs text-gray-500 mb-4">Surf greets them by this name inside the demo.</p>
            <label className="block text-sm font-medium text-gray-700 mb-1">Their website (optional)</label>
            <input type="text" value={demoForm.website} onChange={(e) => setDemoForm({ ...demoForm, website: e.target.value })}
              className="w-full mb-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="theircompany.com" />
            <p className="text-xs text-gray-500 mb-4">With a website, one campaign is written from it (about a minute and a half, in the background). Without one the account starts empty.</p>
            <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <div className="flex gap-2 mb-1">
              <input type="text" required minLength={10} value={demoForm.password} onChange={(e) => setDemoForm({ ...demoForm, password: e.target.value })}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono" />
              <button type="button" onClick={() => setDemoForm({ ...demoForm, password: generatePassword() })} className="px-3 py-2 text-xs border border-gray-300 rounded-lg hover:bg-gray-50">Generate</button>
            </div>
            <div className="mb-4"><CopyButton text={demoForm.password} label="Copy password" /></div>
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Note (only you see this)</label>
                <input type="text" maxLength={200} value={demoForm.note} onChange={(e) => setDemoForm({ ...demoForm, note: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="e.g. met at the expo" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Days</label>
                <input type="number" min={1} max={90} value={demoForm.expires_days} onChange={(e) => setDemoForm({ ...demoForm, expires_days: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              </div>
            </div>
            <p className="text-xs text-gray-500 mb-4">Messaging is permanently off and there is no billing. The demo is deleted automatically about a day after it expires.</p>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setShowDemo(false)} disabled={creating} className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
              <button type="submit" disabled={creating} className="px-4 py-2 text-sm bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg disabled:opacity-60">{creating ? 'Creating…' : 'Create demo'}</button>
            </div>
          </form>
        </div>
      )}

      {extendTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-sm bg-white rounded-xl shadow-xl p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Extend this account</h2>
            <p className="text-sm text-gray-600 mb-4"><strong>{extendTarget.email}</strong> is open until {fmtDate(extendTarget.expires_at)}. Days are added to whichever is later, today or that date (never more than 90 days from today).</p>
            <label className="block text-sm font-medium text-gray-700 mb-1">Add days</label>
            <input type="number" min={1} max={90} value={extendDays} onChange={(e) => setExtendDays(e.target.value)}
              className="w-full mb-4 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
            <div className="flex justify-end gap-2">
              <button onClick={() => setExtendTarget(null)} className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={doExtend} disabled={busyId === extendTarget.tenant_id} className="px-4 py-2 text-sm bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg disabled:opacity-60">
                {busyId === extendTarget.tenant_id ? 'Extending…' : 'Extend'}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md bg-white rounded-xl shadow-xl p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Delete this account?</h2>
            <p className="text-sm text-gray-600 mb-4">
              <strong>{confirmDelete.email}</strong> will lose access immediately and everything in their sandbox (campaigns, leads, conversations) is permanently deleted. This cannot be undone.
            </p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmDelete(null)} className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={doDelete} disabled={busyId === confirmDelete.tenant_id} className="px-4 py-2 text-sm bg-red-600 hover:bg-red-700 text-white rounded-lg disabled:opacity-60">
                {busyId === confirmDelete.tenant_id ? 'Deleting…' : 'Delete permanently'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
